import { Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPrompt, DialogPromptApi, DialogPromptTypeSpeakV2 } from "../../api/DialogPromptApi";
import AppBarContext from '../../context/AppBarContext';
import { useSentenceChinesePinyin } from "../../hook/useSentenceChinesePinyin";
import { useStudentResponsePinyin } from "../../hook/useStudentResponsePinyin";
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPromptTypeSpeakV2FormProps {
    oldDialogPromptId: number;
    setNewDialogPrompt: React.Dispatch<React.SetStateAction<DialogPrompt | null>>;
    id: number;
    dialogId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    isHidden: boolean;
    yesResponse: string | null;
    imageSource: string | null;
    imageSourceUrl: string | null;
}

export const DialogPromptTypeSpeakV2Form: React.FC<DialogPromptTypeSpeakV2FormProps> = ({
    oldDialogPromptId, setNewDialogPrompt,
    id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, imageSource, imageSourceUrl }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const {
        text: teacherChinesePrompt,
        pinyin: teacherChinesePromptPinyin,
        pinyinList: teacherChinesePromptPinyinList,
        pinyinErrorText: teacherChinesePromptPinyinErrorText,
        setDialogPromptFields: setTeacherChinesePromptFields,
        clearDialogPromptFields: clearTeacherChinesePromptFields,
        clearErrorText: clearTeacherChinesePromptPinyinErrorText,
        handleTextChange: handleTeacherChinesePromptChange,
        handlePinyinChange: handleTeacherChinesePromptPinyinChange,
    } = useSentenceChinesePinyin({ history, location, setErrorMessage });

    const {
        studentResponsePrompt,
        studentResponsePromptErrorText,
        // studentResponseList,
        // studentResponseListErrorText,
        // studentResponsePinyinList,
        // studentResponsePinyinErrorText,
        setStudentResponseFields,
        clearStudentResponseFields,
        clearStudentResponseErrors,
        handleStudentResponsePromptChange,
        // handleStudentResponseListChange,
        // handleStudentResponsePinyinListChange,
    } = useStudentResponsePinyin({ history, location, setErrorMessage });

    const [teacherAskStudentToSpeakPrompt, setTeacherAskStudentToSpeakPrompt] = React.useState("");
    const [spokenYesResponse, setSpokenYesResponse] = React.useState("");
    const [spokenNoResponse, setSpokenNoResponse] = React.useState("");


    const handleTeacherAskStudentToSpeakPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTeacherAskStudentToSpeakPrompt(e.target.value); };
    const handleSpokenYesResponseChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSpokenYesResponse(e.target.value); };
    const handleSpokenNoResponseChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSpokenNoResponse(e.target.value); };

    const setDialogPromptFields = React.useCallback((dialogPrompt: DialogPromptTypeSpeakV2) => {
        if (dialogPrompt.class_name == "DialogPromptTypeSpeakV2Model") {

            setTeacherChinesePromptFields(dialogPrompt.teacher_chinese_prompt, dialogPrompt.teacher_chinese_prompt_pinyin, dialogPrompt.teacher_chinese_prompt_sentence_id)

            setStudentResponseFields(dialogPrompt.student_response_prompt, [])

            setTeacherAskStudentToSpeakPrompt(dialogPrompt.teacher_ask_student_to_speak_prompt ? dialogPrompt.teacher_ask_student_to_speak_prompt : "")
            setSpokenYesResponse(dialogPrompt.spoken_yes_response ? dialogPrompt.spoken_yes_response : "")
            setSpokenNoResponse(dialogPrompt.spoken_no_response ? dialogPrompt.spoken_no_response : "")
        } else {
            clearTeacherChinesePromptFields()
            clearStudentResponseFields()
            setTeacherAskStudentToSpeakPrompt("");
            setSpokenYesResponse("");
            setSpokenNoResponse("");
        }
    }, [setTeacherChinesePromptFields, clearTeacherChinesePromptFields, setStudentResponseFields, clearStudentResponseFields, setTeacherAskStudentToSpeakPrompt, setSpokenYesResponse, setSpokenNoResponse])

    React.useEffect(() => {
        DialogPromptApi.getById(oldDialogPromptId)
            .then((response) => {
                const oldDialogPrompt = response.data as DialogPromptTypeSpeakV2;
                setDialogPromptFields(oldDialogPrompt);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        clearTeacherChinesePromptPinyinErrorText()
        clearStudentResponseErrors()
    }, [oldDialogPromptId, clearStudentResponseErrors, clearTeacherChinesePromptPinyinErrorText, history, location, setErrorMessage, setDialogPromptFields])


    React.useEffect(() => {
        const dialogPrompt = new DialogPromptTypeSpeakV2(
            id,
            dialogId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            isHidden,
            yesResponse as string | "ERROR",
            imageSource,
            imageSourceUrl,
            teacherChinesePrompt,
            null, // sentence_id is null
            teacherChinesePromptPinyinList,
            studentResponsePrompt,
            teacherAskStudentToSpeakPrompt,
            spokenYesResponse,
            spokenNoResponse
        );
        setNewDialogPrompt(dialogPrompt);
    }, [id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, imageSource, imageSourceUrl,
        teacherChinesePrompt, teacherChinesePromptPinyinList, studentResponsePrompt,
        teacherAskStudentToSpeakPrompt, spokenYesResponse, spokenNoResponse, setNewDialogPrompt])

    return (
        <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>

            <TextField
                label="Teacher Chinese Prompt"
                value={teacherChinesePrompt}
                onChange={handleTeacherChinesePromptChange}
            />
            <TextField
                label="Teacher Chinese Prompt Pinyin"
                value={teacherChinesePromptPinyin}
                onChange={handleTeacherChinesePromptPinyinChange}
                error={teacherChinesePromptPinyinErrorText != null}
                helperText={teacherChinesePromptPinyinErrorText}
            />
            <div>
                <TextField
                    label="Student Chinese Prompt"
                    value={studentResponsePrompt ? studentResponsePrompt : "<Answer/>"}
                    onChange={handleStudentResponsePromptChange}
                    required
                    sx={{ width: '100%' }}
                    error={studentResponsePromptErrorText != null}
                    helperText={studentResponsePromptErrorText}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Use &lt;Answer/&gt; to mark where the response is inserted.<br />
                    The prompt will be skipped and not displayed, if this field is only &lt;Answer/&gt;
                </Typography>
            </div>
            <TextField
                label="Teacher Ask Student To Speak Prompt"
                value={teacherAskStudentToSpeakPrompt}
                onChange={handleTeacherAskStudentToSpeakPromptChange}
                required
            />
            <TextField
                label="Student Spoken Well Prompt"
                value={spokenYesResponse}
                onChange={handleSpokenYesResponseChange}
                required
            />
            <TextField
                label="Student Spoken Poorly Prompt"
                value={spokenNoResponse}
                onChange={handleSpokenNoResponseChange}
                required
            />
        </Stack>
    )
}
